<script setup>
import { computed } from 'vue';

import Button from 'dashboard/components-next/button/Button.vue';
import WootLabel from 'dashboard/components-next/label/Label.vue';
import {
  BaseTable,
  BaseTableRow,
  BaseTableCell,
} from 'dashboard/components-next/table';

import { useAutomationOperationsFormatters } from './composables/useAutomationOperationsFormatters';
import { cadenceActionKey } from './utils/automationOperationsFormatters';

const props = defineProps({
  enrollments: { type: Array, default: () => [] },
  nextExecutions: { type: Array, default: () => [] },
  isLoading: { type: Boolean, default: false },
  canOperate: { type: Boolean, default: false },
  actionLoading: { type: Object, default: () => ({}) },
});

defineEmits(['transition']);

const {
  t,
  cadenceEnrollmentState,
  cadenceLastTransition,
  cadenceVersionLabel,
  contactReference,
  formatDate,
  formatStatus,
  labelColor,
  nextExecutionLabel,
} = useAutomationOperationsFormatters();

const cadenceHeaders = computed(() => [
  t('AUTOMATION.OPERATIONS.CADENCES.TABLE_HEADER.CONTACT'),
  t('AUTOMATION.OPERATIONS.CADENCES.TABLE_HEADER.CADENCE'),
  t('AUTOMATION.OPERATIONS.CADENCES.TABLE_HEADER.STATE'),
  t('AUTOMATION.OPERATIONS.CADENCES.TABLE_HEADER.NEXT_ACTION'),
  t('AUTOMATION.OPERATIONS.CADENCES.TABLE_HEADER.VERSION'),
  t('AUTOMATION.OPERATIONS.CADENCES.TABLE_HEADER.ACTIONS'),
]);

const isCadenceActionLoading = (id, actionName) =>
  Boolean(props.actionLoading[cadenceActionKey(id, actionName)]);
</script>

<template>
  <BaseTable
    :headers="cadenceHeaders"
    :items="enrollments"
    :loading="isLoading"
    :loading-message="$t('AUTOMATION.OPERATIONS.CADENCES.LOADING')"
    :no-data-message="$t('AUTOMATION.OPERATIONS.CADENCES.EMPTY')"
  >
    <template #row="{ items }">
      <BaseTableRow
        v-for="enrollment in items"
        :key="enrollment.id"
        :item="enrollment"
        :state="cadenceEnrollmentState(enrollment)"
      >
        <template #default>
          <BaseTableCell class="min-w-48">
            <div class="flex min-w-0 flex-col gap-1">
              <span class="truncate text-body-main text-n-slate-12">
                {{ enrollment.contact?.label || '-' }}
              </span>
              <span class="text-body-main text-n-slate-11">
                {{ contactReference(enrollment) }}
              </span>
            </div>
          </BaseTableCell>
          <BaseTableCell class="min-w-52">
            <div class="flex min-w-0 flex-col gap-1">
              <span class="truncate text-body-main text-n-slate-12">
                {{ enrollment.cadence?.name || '-' }}
              </span>
              <span class="truncate text-body-main text-n-slate-11">
                {{ enrollment.cadence?.purpose || '-' }}
              </span>
            </div>
          </BaseTableCell>
          <BaseTableCell class="min-w-44">
            <div class="flex min-w-0 flex-col gap-1">
              <div class="flex flex-wrap items-center gap-2">
                <WootLabel
                  :label="formatStatus(enrollment.status)"
                  :color="labelColor(enrollment.status)"
                  compact
                />
                <WootLabel
                  v-if="enrollment.due"
                  :label="$t('AUTOMATION.OPERATIONS.CADENCES.DUE_NOW')"
                  color="amber"
                  compact
                />
              </div>
              <span class="truncate text-body-main text-n-slate-11">
                {{
                  enrollment.status_reason || cadenceLastTransition(enrollment)
                }}
              </span>
            </div>
          </BaseTableCell>
          <BaseTableCell class="min-w-52">
            <div class="flex min-w-0 flex-col gap-1">
              <span class="truncate text-body-main text-n-slate-12">
                {{ formatStatus(enrollment.next_action?.type) }}
              </span>
              <span class="text-body-main text-n-slate-11">
                {{ formatDate(enrollment.next_wakeup_at) }}
              </span>
              <span
                v-if="enrollment.timers?.claim_active"
                class="truncate text-body-main text-n-slate-11"
              >
                {{
                  $t('AUTOMATION.OPERATIONS.CADENCES.LEASED_UNTIL', {
                    at: formatDate(enrollment.timers?.claim_expires_at),
                  })
                }}
              </span>
            </div>
          </BaseTableCell>
          <BaseTableCell class="min-w-44">
            <div class="flex min-w-0 flex-col gap-1">
              <span class="text-body-main text-n-slate-12">
                {{ cadenceVersionLabel(enrollment) }}
              </span>
              <WootLabel
                v-if="enrollment.version?.old_version"
                :label="$t('AUTOMATION.OPERATIONS.CADENCES.VERSION_OLD')"
                color="amber"
                compact
              />
            </div>
          </BaseTableCell>
          <BaseTableCell align="end" class="min-w-44">
            <div class="flex flex-wrap justify-end gap-2">
              <Button
                v-if="enrollment.status === 'active'"
                :label="$t('AUTOMATION.OPERATIONS.CADENCES.PAUSE')"
                :data-test="`cadence-pause-${enrollment.id}`"
                icon="i-lucide-pause"
                color="amber"
                variant="outline"
                size="sm"
                :disabled="!canOperate"
                :is-loading="isCadenceActionLoading(enrollment.id, 'pause')"
                @click="$emit('transition', enrollment, 'pause')"
              />
              <Button
                v-if="enrollment.status === 'paused'"
                :label="$t('AUTOMATION.OPERATIONS.CADENCES.RESUME')"
                :data-test="`cadence-resume-${enrollment.id}`"
                icon="i-lucide-play"
                color="teal"
                variant="outline"
                size="sm"
                :disabled="!canOperate"
                :is-loading="isCadenceActionLoading(enrollment.id, 'resume')"
                @click="$emit('transition', enrollment, 'resume')"
              />
            </div>
          </BaseTableCell>
        </template>
      </BaseTableRow>
    </template>
  </BaseTable>

  <div
    v-if="nextExecutions.length"
    class="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-body-main text-n-slate-11"
  >
    <span
      v-for="execution in nextExecutions"
      :key="execution.id"
      class="truncate"
    >
      {{ nextExecutionLabel(execution) }}
    </span>
  </div>
</template>
